import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { AsyncReturnService } from '../../../../../shared';
import { CartEntryFullPalletNoteConnector } from '../../connector/cart-entry-full-pallet-note.connector';
import { CartEntryFullPalletNoteActionTypes } from '../actions/cart-entry-full-pallet-note.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryFullPalletNoteEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  addFullPalletNote$: Observable<
    CartActions.AddCartEntryFullPalletNoteSuccess | CartActions.AddCartEntryFullPalletNoteFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryFullPalletNoteActionTypes.AddCartEntryFullPalletNote),
      map((action: CartActions.AddCartEntryFullPalletNote) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.add(payload.userId, payload.cartId, payload.entry).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.AddCartEntryFullPalletNoteSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.AddCartEntryFullPalletNoteFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForAddFullPalletNote$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryFullPalletNoteActionTypes.AddCartEntryFullPalletNoteFail),
      map((action: CartActions.AddCartEntryFullPalletNoteFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.addCartEntryFullPalletNoteFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeFullPalletNote$: Observable<
    CartActions.RemoveCartEntryFullPalletNoteSuccess | CartActions.RemoveCartEntryFullPalletNoteFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryFullPalletNoteActionTypes.RemoveCartEntryFullPalletNote),
      map((action: CartActions.RemoveCartEntryFullPalletNote) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.remove(payload.userId, payload.cartId, payload.entry).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.RemoveCartEntryFullPalletNoteSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.RemoveCartEntryFullPalletNoteFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForRemoveFullPalletNote$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryFullPalletNoteActionTypes.RemoveCartEntryFullPalletNoteFail),
      map((action: CartActions.RemoveCartEntryFullPalletNoteFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryFullPalletNoteFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartEntryFullPalletNoteConnector,
    private asyncReturnService: AsyncReturnService
  ) {}
}
